import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { NotificationService } from '@service/notification.service';
import { getUserLocal, LocalSettingsKey } from '@shared/types/local-settings';
import { LoginService } from '@service/rest/login/login.service';
import { catchError, first, map, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LogoutService } from '../login/logout.service';
import { SessionUtilsService } from '@service/rest/login/session-utils.service';

@Injectable({
  providedIn: 'root'
})
export class RefreshTokenService {
  private tokenRenew: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private tokenRenewOnCurrentTab: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private notificationService: NotificationService,
    private loginService: LoginService,
    private translateService: TranslateService,
    private logoutService: LogoutService,
    private sessionUtilsService: SessionUtilsService
  ) {
    const _self = this;
    window.addEventListener('storage', function (event) {
      if (event.key === LocalSettingsKey.IS_TOKEN_RENEWING) {
        _self.tokenRenewing(!!event.newValue);
      }
    });
  }

  checkTokens(): void {
    if (!SessionUtilsService.tokenHasTimeByType()) { // access_tokenin süresi var mı?
      if (SessionUtilsService.tokenHasTimeByType('refresh')) { // refresh tokenin süresi var mı?
        this.refreshToken()?.pipe(first(), catchError((err) => {
          localStorage.removeItem(LocalSettingsKey.IS_TOKEN_RENEWING);
          this.notificationService.showError(this.translateService.instant(err.error.message));
          return of(err);
        })).subscribe();
      } else {
        this.logoutService.clearAfterLogout();
      }
    }
  }

  checkAccessTokenForInit(): Promise<Object> {
    return new Promise(resolve => {
      const user = getUserLocal();
      const hasRefreshTokenTime = SessionUtilsService.tokenHasTimeByType('refresh');

      if ((!user?.token || !SessionUtilsService.tokenHasTimeByType()) && (user?.refresh_token && hasRefreshTokenTime)) {
        // userın access_tokeni yoksa yada süresi bitmişse; refresh_tokeni ve refresh_tokenin süresi varsa yeni token üretiyoruz.
        const refreshTokenObs = this.refreshToken();
        if (refreshTokenObs) {
          refreshTokenObs.pipe(tap(() => {
            localStorage.removeItem(LocalSettingsKey.IS_TOKEN_RENEWING);
            resolve(true);
          }), catchError(err => {
            localStorage.removeItem(LocalSettingsKey.IS_TOKEN_RENEWING);
            this.logoutService.clearAfterLogout();
            resolve(true);
            return of(err);
          })).subscribe();
        } else {
          resolve(true);
        }
      } else if (user?.refresh_token && !hasRefreshTokenTime) {
        // refresh token varsa ve süresi bittiyse tekrar login olması lazım.
        this.notificationService.showError(this.translateService.instant('REFRESH_TOKEN_EXPIRED'));
        this.logoutService.clearAfterLogout();
        return resolve(true);
      } else {
        // burda clearAfterLogout yapmıyoruz çünkü proje ayağa kalktıktan sonra da login üzerinden user datası set edilebilir.
        return resolve(true);
      }
    });
  }

  refreshToken(isHardRefresh = false): Observable<void> {
    // farklı tabda token yenileniyor
    if (!isHardRefresh && localStorage.getItem(LocalSettingsKey.IS_TOKEN_RENEWING)) {
      return;
    }

    localStorage.setItem(LocalSettingsKey.IS_TOKEN_RENEWING, '1');
    this.tokenRenewing(true);
    this.tokenRenewingOnCurrentTab(true);
    return this.loginService.refreshToken().pipe(first(), map(res => {
      this.sessionUtilsService.setNewTokensToLocale(res);
      localStorage.removeItem(LocalSettingsKey.IS_TOKEN_RENEWING);
      this.tokenRenewing(false);
      this.tokenRenewingOnCurrentTab(false);
      return res;
    }), catchError((err) => {
      this.logoutService.clearAfterLogout();
      this.tokenRenewing(false);
      this.tokenRenewingOnCurrentTab(false);
      localStorage.removeItem(LocalSettingsKey.IS_TOKEN_RENEWING);
      return of(err);
    }));
  }

  tokenRenewing(flag: boolean): void {
    if (!flag) {
      localStorage.removeItem(LocalSettingsKey.IS_TOKEN_RENEWING);
    }
    this.tokenRenew.next(flag);
  }

  tokenRenewingOnCurrentTab(flag: boolean): void {
    this.tokenRenewOnCurrentTab.next(flag);
  }

  isTokenRenewingOnCurrentTab(): boolean {
    return this.tokenRenewOnCurrentTab.getValue();
  }

  isTokenRenewing(): boolean {
    return this.tokenRenew.getValue();
  }

  isTokenRenewingPromise(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.tokenRenew.pipe(first()).subscribe(res => {
        resolve(true);
      });
    });
  }

}
